import React, { Dispatch, ReactElement, SetStateAction, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import { RaptorTheme } from '../../styling/theme';
import {
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';

const useStyles = makeStyles<RaptorTheme, StyleProps>((theme: RaptorTheme) => ({
  formControl: {
    margin: (props) => (props.margin ? props.margin : theme.spacing(1)),
    marginBottom: 0,
    minWidth: 120,
    flexGrow: 1,
    '& label': {
      width: '9.38rem',
    },
  },
  selectRoot: {
    maxWidth: '100%',
    color: (props) => props.placeholderColor,
    fontSize: (props) => props.fontSize || '0.88rem',
    height: '100%',
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  notchedOutline: {
    backgroundColor: 'red',
    borderColor: 'yellow !important',
  },
  testInput: {
    // padding: "0.62rem",
  },
  pickerRoot: {
    marginRight: '0.31rem',
  },
  noneSelectedRoot: {
    color: (props) => props.placeholderColor,
  },
}));

export interface MenuChoice {
  value: string;
  label: string;
}
interface Props<T> {
  labels: ReadonlyArray<string>;
  values: ReadonlyArray<T>;
  setSelected: (
    selected: T,
  ) => void | Dispatch<SetStateAction<T>> | Promise<void>;
  selected: T;
  placeholderValue?: string | null;
  selectTitle?: string | React.ReactNode;
  variant?: 'filled' | 'standard' | undefined | 'outlined';
  customMenuItem?: React.FunctionComponent<CustomMenuItemProps>;
  disableUnderline?: boolean;
  placeholderColor?: string;
  fundName?: string;
  margin?: string;
  fontSize?: string | number;
  label?: string;
  // these custom styles allow us to define useStyles in the parent component, and then
  // pass the style specifier string down to the child component
  customStyles?: {
    formControl?: string;
    selectRoot?: string;
    selectOutlined?: string;
    pickerRoot?: string;
    menuItem?: string;
  };
  middleLabelValue?: string;
  // the labelTitle is if we want to give some information, e.g View Type in the case of asset class
  labelTitle?: string;
  truncateLabels?: boolean;
}

interface StyleProps {
  placeholderColor: string;
  margin?: string;
  fontSize: string | number | null;
}

export interface CustomMenuItemProps {
  label: string;
  value: string;
  fundName?: string | null;
}

export function labelTruncator(input: string) {
  let string = input;
  if (string.length > 25) {
    string = string.substring(0, 24) + '...';
  }
  return string;
}

function GeneralSelect<T extends string>({
  labels,
  values,
  selected,
  setSelected,
  placeholderValue,
  ...props
}: Props<T>): ReactElement {
  const classes = useStyles({
    placeholderColor: props.placeholderColor || 'red',
    margin: props.margin ? props.margin : null,
    fontSize: props.fontSize || null,
  } as StyleProps);

  const [open, setOpen] = useState(false);
  const handleChange = (event: SelectChangeEvent<T>) => {
    setSelected(event.target.value as T);
  };

  const selectChange = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };


  const SelectMenuItem = props.customMenuItem ? props.customMenuItem : MenuItem;
  return (
    <div className={props.customStyles?.pickerRoot || classes.pickerRoot}>
      {props.middleLabelValue && (
        <Typography
          style={{ marginLeft: '0.50rem', lineHeight: '1px' }}
          variant="subtitle1"
        >
          {props.middleLabelValue}
        </Typography>
      )}
      <FormControl
        variant={props.variant ? props.variant : 'standard'}
        className={props.customStyles?.formControl || classes.formControl}
        style={{ minWidth: 160, maxWidth: '100%', position: 'relative' }}
      >
        {props.selectTitle && <InputLabel>{props.selectTitle}</InputLabel>}
        <div style={{ position: 'relative', width: '100%' }}>
          <Select
            onOpen={handleOpen}
            onClose={handleClose}
            disableUnderline
            onClick={selectChange}
            id="demo-simple-select-outlined"
            value={selected}
            onChange={handleChange}
            label={props.selectTitle ? props.selectTitle : ''}
            classes={{
              root: props.customStyles?.selectRoot || classes.selectRoot,
              outlined: props.customStyles?.selectOutlined || classes.testInput,
            }}
          >
          {placeholderValue && (
            <MenuItem
              value="none_selected"
              classes={{
                root: classes.noneSelectedRoot,
              }}
            >
              {placeholderValue}
            </MenuItem>
          )}
          {labels.map((label, index: number) => (
            <SelectMenuItem
              fundName={props.fundName}
              key={label + values[index]}
              label={label}
              value={values[index] as string}
              className={props.customStyles?.menuItem}
            >
              {props.truncateLabels && label === selected && !open
                ? labelTruncator(label)
                : label}
            </SelectMenuItem>
          ))}
          </Select>
        </div>
      </FormControl>
    </div>
  );
}

export default GeneralSelect;
